import clsx from 'clsx';
import { HTMLAttributes, forwardRef } from 'react';

type Variant =
  | 'H1'
  | 'H2'
  | 'H3'
  | 'H4'
  | 'H5'
  | 'p1'
  | 'p2'
  | 'p3'
  | 'buttons-1'
  | 'buttons-2'
  | 'base';
type FontWeight = 300 | 400 | 500 | 600 | 700 | 800;

type CustomProps = {
  variant?: Variant;
  fontWeight?: FontWeight;
  color?: string;
  truncate?: boolean;
};

interface ParagraphProps
  extends React.DetailedHTMLProps<HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>,
    CustomProps {}

const Typography = forwardRef<HTMLParagraphElement, ParagraphProps>(
  (
    {
      className,
      children,
      variant = 'p1',
      color = 'text-primary-900',
      truncate,
      fontWeight,
      ...rest
    },
    ref
  ) => {
    return (
      <p
        ref={ref}
        className={clsx(
          variantStyleMap[variant],
          fontWeight ? fontWeightStyleMap[fontWeight] : undefined,
          { 'overflow-hidden text-ellipsis max-w-full whitespace-nowrap': truncate },
          color,
          className
        )}
        {...rest}
      >
        {children}
      </p>
    );
  }
);
Typography.displayName = 'Typography';

export default Typography;

const variantStyleMap: { [k in Variant]: string } = {
  H1: 'text-[60px] leading-[70px]',
  H2: 'text-[30px] leading-[30px]',
  H3: 'text-[24px] leading-[24px]',
  H4: 'text-[20px] leading-[24px]',
  H5: 'text-[18px] leading-[24px] font-medium',
  p1: 'text-[18px] leading-[20px]',
  p2: 'text-[16px] leading-[24px]',
  p3: 'text-[14px] leading-[20px] font-medium',
  'buttons-1': 'text-[18px] leading-[22px] font-semibold',
  'buttons-2': 'text-[12px] leading-[16px]',
  base: 'text-[14px] leading-[19.6px] font-medium',
};

const fontWeightStyleMap: { [k in FontWeight]: string } = {
  300: 'font-light',
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
  800: 'font-extrabold',
};
